import { MODELS_CLASS_NAMES } from '@/config/research';
import Vue from 'vue';

export const ProjectSubject = {
  namespaced: true,

  state: {
    subject: {},
    loading: false,
    loadingData: false,
    saving: false,
    subjectData: {},
    dicomStudies: [],
    segmentationsSubjectData: [],
  },

  getters: {
    isSubjectLoaded: (state) => {
      return state.subject && Object.keys(state.subject).includes('uuid');
    },

    isSubjectDataLoaded: (state) => {
      return state.subjectData && Object.keys(state.subjectData).length !== 0;
    },

    dataSet: (state) => {
      return state.dataSet;
    },

    subject: (state) => {
      return state.subject;
    },

    subjectData: (state) => {
      return state.subjectData;
    },

    isLoading: (state) => {
      return state.loading;
    },

    isLoadingData: (state) => (id) => {
      return state.loadingData[id] ?? false;
    },

    dicomStudySeries(state) {
      return state.dicomStudies;
    },
  },

  mutations: {
    addSubject(state, subject) {
      state.subject = subject;
    },

    resetSubject(state) {
      state.subject = {};
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setLoadingData(state, loading) {
      state.loadingData = { ...state.loadingData, ...loading };
    },

    setSubjectData(state, subjectData) {
      state.subjectData = subjectData;
    },

    setData(state, dataSet) {
      state.dataSet = dataSet;
    },
    setSegmentationsSubjectData(state, payload) {
      state.segmentationsSubjectData = payload;
    },

    toggleRowDetails(state, { item, value }) {
      const subjectDataItemIndex = state.subjectData.findIndex(({ id }) => id === item.id);
      Vue.set(state.subjectData[subjectDataItemIndex], '_showDetails', value);
    },

    setItemDetails(state, { item, itemDetails }) {
      const i = state.subjectData.findIndex(({ id }) => id === item.id);
      Vue.set(state.subjectData[i], 'details', itemDetails);
    },

    setStudiesCluster(state, studies) {
      state.dicomStudies = studies;
    },
  },

  actions: {
    async getSubject(context, params) {
      let [repositoryUuid, projectUuid, subjectUuid, eventUuid] = params;
      const response = await this._vm.$api('repositories.projects.subjects.show', {
        version: '1.0',
        repository: repositoryUuid,
        project: projectUuid,
        subject: subjectUuid,
        event_uuid: eventUuid,
      });
      context.commit('addSubject', response.data.data);
    },

    async loadSubjectDataSets(
      { commit },
      {
        project,
        repository,
        subject,
        ancestors,
        pipelineVersion,
        allEventsVersions,
        downloadable = false,
        event_uuid,
        event_version,
        signed_only,
        filter,
        sortBy,
        sortDesc,
      }
    ) {
      if (!project || !repository || !subject) {
        return;
      }

      let subjectData = null;

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.subjects.dataSets.index', {
          version: '1.0',
          project,
          repository,
          subject,
          events: ancestors ?? [],
          pipeline_version: pipelineVersion ?? null,
          all_events_versions: allEventsVersions ?? null,
          event_status: 1,
          downloadable,
          event_uuid,
          event_version,
          signed_only,
          filter,
          sortBy,
          sortDesc,
        });

        subjectData = response?.data?.data;

        const segmentations = subjectData?.filter(
          (study) => study.data.className === MODELS_CLASS_NAMES.RESEARCH_SEGMENTATION_INSTANCE
        );

        commit('setSubjectData', subjectData);
        commit('setSegmentationsSubjectData', segmentations);
        commit('setLoading', false);
      } catch (err) {
        commit('setLoading', false);
        console.error(err);
      }
    },

    async loadDataSet({ commit }, { project, repository, subject, item, pipelineVersion, scope }) {
      let itemDetails = null;

      let value = true;
      commit('setLoadingData', { [item.id]: value });
      commit('toggleRowDetails', { item, value });
      try {
        if (item.data.className === MODELS_CLASS_NAMES.FORM_INSTANCE) {
          const response = await this._vm.$api('repositories.projects.subjects.data.sets', {
            pipeline_version: pipelineVersion ?? null,
            moduleConnector: item.module.id,
            version: '1.0',
            repository,
            project,
            subject,
          });

          itemDetails = response.data;
        } else if (item.data.className === MODELS_CLASS_NAMES.DICOM_CASE) {
          if (item.data.segmentations.some((seg) => seg.isExternalSegmentation)) {
            itemDetails = {
              componentScope: scope,
              component: 'DataOrganismDicomCaseRowDetailsMolecule',
              componentProps: {
                value: item.data.seriesImages ?? [],
                externalSegsProps: {
                  componentScope: scope,
                  component: 'DataOrganismResearchSegmentationInstanceRowDetailsMolecule',
                  componentProps: {
                    value: item.data.segmentations ?? [],
                  },
                },
              },
            };
          } else {
            itemDetails = {
              componentScope: scope,
              component: 'DataOrganismDicomCaseRowDetailsMolecule',
              componentProps: {
                value: item.data.seriesImages ?? [],
              },
            };
          }
        } else if (item.data.className === MODELS_CLASS_NAMES.RESEARCH_SEGMENTATION_INSTANCE) {
          itemDetails = {
            componentScope: scope,
            component: 'DataOrganismResearchSegmentationInstanceRowDetailsMolecule',
            componentProps: {
              value: item.data.segmentations ?? [],
            },
          };
        } else if (item.data.className === MODELS_CLASS_NAMES.RESEARCH_TOOL_INSTANCE) {
          const baseProps = {
            value: item.data.data ?? [],
            module: item.module,
            instance: item.data,
            instanceId: item.data.repository_tool_instance_id,
            subject: item.event_run.subject,
            event: item.event_run.event,
            repositoryUuid: repository,
            version: item.version,
            subjectUuid: subject,
            projectUuid: project,
            ancestors: item.eventModuleData?.extraAttributes?.ancestors ?? [],
          };

          if (item.data.segmentations.some((seg) => seg.isExternalSegmentation)) {
            itemDetails = {
              componentScope: scope,
              component: 'DataOrganismDicomCaseRowDetailsMolecule',
              componentProps: {
                ...baseProps,
                externalSegsProps: {
                  componentScope: scope,
                  component: 'DataOrganismResearchSegmentationInstanceRowDetailsMolecule',
                  componentProps: {
                    value: item.data.segmentations ?? [],
                  },
                },
              },
            };
          } else {
            itemDetails = {
              componentScope: scope,
              component: 'DataOrganismResearchToolInstanceRowDetailsMolecule',
              componentProps: baseProps,
            };
          }
        }
        commit('setItemDetails', { item, itemDetails });
      } catch (err) {
        console.error(err);
      }

      value = false;
      commit('setLoadingData', { [item.id]: value });
    },

    toggleRowDetails({ commit }, { item, value }) {
      commit('toggleRowDetails', { item, value });
    },

    resetSubject({ commit }) {
      commit('resetSubject');
    },

    addSubject({ commit }, subject) {
      commit('addSubject', subject);
    },

    resetSubjectData({ commit }) {
      commit('setSubjectData', {});
    },

    addStudiesCluster({ commit }, studies) {
      commit('setStudiesCluster', studies);
    },
  },
};
